import { io } from "socket.io-client";
import { getAuth } from "firebase/auth";
import getFirebase from "../../firebase";
import { getDevToken } from "../utils/commHelper";

const SOCKET_URL_PROD = "https://websocket.tulu.io";
const SOCKET_URL_DEV = "https://websocket.dev.tulu.io";

let socket = null;
let connecting = false;
let expiredTokenFailures = 0;
const MAX_EXPIRED_TOKEN_RETRIES = 5;

function generateDeviceId() {
  let id = localStorage.getItem("printers-web-device-id");
  if (!id) {
    id = `printers-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    localStorage.setItem("printers-web-device-id", id);
  }
  return id;
}

async function resolveToken(forceRefresh = false) {
  const devToken = getDevToken();
  if (devToken) return devToken;
  // Only touch Firebase Auth when no pasted dev token is present.
  try {
    const auth = getAuth(getFirebase());
    // <Login> can render children before Firebase Auth finishes restoring its
    // session (it only checks a cached localStorage user) — wait for the SDK's
    // initial auth state before reading currentUser, or this silently connects
    // with no token (or never connects at all) on a fresh page load.
    await auth.authStateReady();
    const user = auth.currentUser;
    if (!user) return null;
    return user.getIdToken(forceRefresh);
  } catch (e) {
    console.warn("[PrintersSocket] getAuth failed:", e?.message || e);
    return null;
  }
}

/**
 * Connect to the printers-web socket bundle.
 * @param {boolean} isDev — use websocket.dev when true
 */
export async function connectPrintersSocket(isDev = false) {
  if (socket?.connected) return socket;
  if (connecting) return null;
  connecting = true;

  try {
    const token = await resolveToken();
    if (!token) {
      connecting = false;
      return null;
    }

    if (socket) {
      socket.disconnect();
      socket = null;
    }

    const deviceId = generateDeviceId();
    // Governed ONLY by the explicit isDev toggle — matching getPost/getRequest exactly.
    // Folding in isDevMode() (NODE_ENV==='development', i.e. always true under `npm
    // start`) used to force the socket onto websocket.dev.tulu.io while the REST
    // snapshot kept hitting api.tulu.io (prod) whenever the in-page toggle was off —
    // two different backends, so nothing ever arrived live even though the initial
    // snapshot looked correct. localhost is explicitly CORS-allowed against BOTH
    // socket servers (tulu_socket_server), so there's no reason to special-case it.
    const socketUrl = isDev ? SOCKET_URL_DEV : SOCKET_URL_PROD;

    socket = io(socketUrl, {
      query: {
        authorization: token,
        device_id: deviceId,
        app_bundle_id: "com.tulou.printers.web",
        reason_to_connect: "printers_dashboard",
      },
      extraHeaders: {
        authorization: token,
        device_id: deviceId,
        app_bundle_id: "com.tulou.printers.web",
        reason_to_connect: "printers_dashboard",
      },
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      forceNew: true,
    });

    socket.on("connect", () => {
      console.log("[PrintersSocket] Connected");
      expiredTokenFailures = 0;
    });

    socket.on("disconnect", (reason) => {
      console.log("[PrintersSocket] Disconnected:", reason);
    });

    socket.on("connect_error", (err) => {
      console.warn("[PrintersSocket] Connection error:", err.message);
    });

    // The Firebase ID token is captured once in `query`/`extraHeaders` above, but
    // socket.io-client's own `reconnection: true` engine reuses that same query on
    // every automatic reconnect attempt — it never re-fetches a token. A tab left
    // open past the token's ~1h expiry (overnight, laptop sleep, long meeting) would
    // otherwise reconnect forever with a dead token and never actually recover.
    // tulu_socket_server re-validates the token on every connection attempt (not
    // just the first) and emits this exact event on failure — mirrors
    // orchestrator-app's socketService.ts handler for the same backend/problem.
    socket.on("expiredToken", async () => {
      expiredTokenFailures++;
      if (expiredTokenFailures > MAX_EXPIRED_TOKEN_RETRIES) {
        console.warn("[PrintersSocket] Token still rejected after retries — giving up. Reload the page to reconnect.");
        socket?.disconnect();
        return;
      }
      const backoffMs = Math.min(60_000, 2_000 * 2 ** (expiredTokenFailures - 1));
      console.log(`[PrintersSocket] Token expired, retry ${expiredTokenFailures}/${MAX_EXPIRED_TOKEN_RETRIES} in ${backoffMs}ms`);
      await new Promise((resolve) => setTimeout(resolve, backoffMs));
      const newToken = await resolveToken(true);
      if (newToken && socket) {
        socket.disconnect();
        socket.io.opts.query = {
          ...socket.io.opts.query,
          authorization: newToken,
        };
        socket.connect();
      } else {
        console.warn("[PrintersSocket] No authenticated user — not reconnecting.");
      }
    });

    connecting = false;
    return socket;
  } catch (err) {
    console.error("[PrintersSocket] Failed to connect:", err);
    connecting = false;
    return null;
  }
}

export function getPrintersSocket() {
  return socket;
}

export function disconnectPrintersSocket() {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

export function onPrintersSocketEvent(event, handler) {
  if (!socket) return () => {};
  socket.on(event, handler);
  return () => {
    socket?.off(event, handler);
  };
}
